import React, { useEffect, useState } from "react";
import "./eventNotice.css";
import { SnackbarUI, useSnackbar } from "../header/snackbarProvider";
import { getEventNoticeInfoById, getEventNoticeList, giveEventFilePreview, getEventFilePreview, submitEventNoticeAdd, submitEventNoticeUpdate, deleteEventNoticeInfoById } from "../../services/event.service";
import { addMonths, format, parse } from "date-fns";
import * as Yup from "yup";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { ErrorMessage, Field, Form, Formik } from "formik";
import ConfirmationDialog from "../header/confirmation.dialog";
import { FaEdit } from "react-icons/fa";
import { FaTrash } from "react-icons/fa6";

const EventNoticeComponent = () => {
  const { showSnackbar, snackbar } = useSnackbar();
  const [eventNoticeList, setEventNoticeList] = useState([]);
  const [filteredEventNoticeList, setFilteredEventNoticeList] = useState([]);
  const [currentAction, setCurrentAction] = useState("list"); // 'list', 'add', 'edit'
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [openEventNoticeConfirmationDialog, setOpenEventNoticeConfirmationDialog] = useState(false);
  const [dialogValues, setDialogValues] = useState({});

  const [startDateFilter, setStartDateFilter] = useState(null);
  const [endDateFilter, setEndDateFilter] = useState(null);
  const [deleteEventNoticeConfirmationDialog, setDeleteEventNoticeConfirmationDialog] = useState(false);
  const [selectedEventNoticeId, setSelectedEventNoticeId] = useState(null);



  // Initialize default date filters to current month start date and end date
  // useEffect(() => {
  //   const currentDate = new Date();
  //   const startOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
  //   const endOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
  //   setStartDateFilter(startOfMonth);
  //   setEndDateFilter(endOfMonth);
  // }, []);




  // Set default date filters to one month beforecurrent month start date and end date one month after
  useEffect(() => {
    const today = new Date();
    const defaultStartDate = addMonths(today, -2);
    const defaultEndDate = addMonths(today, 2);

    setStartDateFilter(defaultStartDate);
    setEndDateFilter(defaultEndDate);
  }, []);




  // Fetch event notices only after filters are set
  useEffect(() => {
    if (startDateFilter && endDateFilter) {
      fetchEventNotices();
    }
  }, [startDateFilter, endDateFilter]);

  const fetchEventNotices = async () => {
    try {
      const activeEventNoticeList = await getEventNoticeList();
      setEventNoticeList(activeEventNoticeList || []);
    } catch (error) {
      console.error("Error fetching event notices:", error);
      showSnackbar("Error fetching event notices", "danger");
    }
  };

  // Filter when list or filters change
  useEffect(() => {
    filterEventNotices(eventNoticeList, startDateFilter, endDateFilter);
  }, [eventNoticeList, startDateFilter, endDateFilter]);




  // Filter events by date range
  const filterEventNotices = (notices, startDate, endDate) => {
    if (!notices || !startDate || !endDate) {
      setFilteredEventNoticeList(notices || []);
      return;
    }

    const filtered = notices.filter(notice => {
      const noticeStartDate = new Date(notice.startDate);
      const noticeEndDate = new Date(notice.endDate);
      return noticeStartDate >= startDate && noticeEndDate <= endDate;
    });

    setFilteredEventNoticeList(filtered);
  };

  // Handle date filter changes
  const handleStartDateChange = (date) => {
    if (date > endDateFilter) {
      showSnackbar("Start date cannot be after end date", "warning");
      return;
    }
    setStartDateFilter(date);
    filterEventNotices(eventNoticeList, date, endDateFilter);
  };

  const handleEndDateChange = (date) => {
    if (date < startDateFilter) {
      showSnackbar("End date cannot be before start date", "warning");
      return;
    }
    setEndDateFilter(date);
    filterEventNotices(eventNoticeList, startDateFilter, date);
  };




  useEffect(() => {
    fetchEventNotices();
  }, []);

  // Validation schema
  const validationSchemaEventNotice = Yup.object().shape({
    message: Yup.string().required("Message is required"),
    startDate: Yup.date().required("Start Date is required"),
    endDate: Yup.date()
      .required("End Date is required")
      .min(Yup.ref("startDate"), "End date must be after start date"),
  });

  // Initial form values
  const initialValuesOfEventNotice = {
    message: "",
    startDate: "",
    endDate: "",
    attachmentFile: null,
  };



  // Handle add click
  const handleAddClick = () => {
    setSelectedEvent(null);
    setCurrentAction("add");
  };

  // Handle edit click
  const handleEditClick = async (eventNoticeId) => {
    try {
      const eventNoticeInfo = await getEventNoticeInfoById(eventNoticeId);
      setSelectedEvent(eventNoticeInfo);
      setCurrentAction("edit");
    } catch (error) {
      console.error("Error fetching event details:", error);
      showSnackbar("Error loading event details", "danger");
    }
  };

  const handleDeleteClick = (eventNoticeId) => {
    setSelectedEventNoticeId(eventNoticeId);
    setDeleteEventNoticeConfirmationDialog(true);
  };

  const handleDeleteEventNoticeDialogClose = () => {
    setDeleteEventNoticeConfirmationDialog(false);
    setSelectedEventNoticeId(null);
  };

  const handleDeleteEventNoticeDialogConfirm = async () => {
    try {
      const result = await deleteEventNoticeInfoById(selectedEventNoticeId);
      if (result.eventNoticeId !== null) {
        await fetchEventNotices();
        showSnackbar("Event notice deleted successfully!", "success");
      } else {
        showSnackbar("Failed to delete event notice.", "danger");
      }
    } catch (error) {
      console.error("Delete failed:", error);
      showSnackbar("An error occurred while deleting event notice.", "danger");
    }
    setDeleteEventNoticeConfirmationDialog(false);
    setSelectedEventNoticeId(null);
  };

  // Handle cancel
  const handleCancel = () => {
    setCurrentAction("list");
  };

  const handleEventNoticeDialogClose = () => {
    setOpenEventNoticeConfirmationDialog(false);
    setIsSubmitting(false);
  };

  const handleEventNoticeSubmit = async (values, { setSubmitting }) => {
    setSubmitting(true);
    setIsSubmitting(true);
    setDialogValues(values);
    setOpenEventNoticeConfirmationDialog(true);
  };

  const handlEventNoticeDialogConfirm = async () => {
    setOpenEventNoticeConfirmationDialog(false);

    try {
      const isUpdate = currentAction === "edit";
      const eventNoticeDto = {
        eventNoticeId: isUpdate ? selectedEvent.eventNoticeId : null,
        message: dialogValues.message,
        startDate: dialogValues.startDate,
        endDate: dialogValues.endDate,
        attachmentFile: dialogValues.attachmentFile,
      };




      const result = isUpdate
        ? await submitEventNoticeUpdate(eventNoticeDto)
        : await submitEventNoticeAdd(eventNoticeDto);

      if (result) {
        await fetchEventNotices();
        setCurrentAction("list");
        showSnackbar(
          isUpdate
            ? "Event notice updated successfully!"
            : "Event notice added successfully!",
          "success"
        );
      } else {
        showSnackbar(
          isUpdate
            ? "Failed to update event notice."
            : "Failed to add event notice.",
          "danger"
        );
      }
    } catch (error) {
      console.error("Error handling event notice:", error);
      showSnackbar("An error occurred while processing event notice.", "danger");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFilePreview = async (eventFileName) => {
    try {
      const previewData = await getEventFilePreview(eventFileName);
      const EXT = eventFileName.split('.').pop();
      giveEventFilePreview(EXT, previewData, eventFileName);
    } catch (error) {
      showSnackbar('Error fetching preview data.', "danger");
    }
  };



  const renderDesktopTable = () => (
    <div className="table-responsive">
      <table className="event-notice-table">
        <thead>
          <tr>
            <th className="masterHeader" style={{ width: "5%" }}>SN</th>
            <th className="masterHeader" style={{ width: "30%" }}>Message</th>
            <th className="masterHeader" style={{ width: "15%" }}>Start Date</th>
            <th className="masterHeader" style={{ width: "15%" }}>End Date</th>
            <th className="masterHeader" style={{ width: "20%" }}>Attachment</th>
            <th className="masterHeader" style={{ width: "15%" }}>Action</th>
          </tr>
        </thead>
        <tbody>
          {filteredEventNoticeList && filteredEventNoticeList.length > 0 ? (
            filteredEventNoticeList.map((eventNotice, index) => (
              <tr key={eventNotice.eventNoticeId}>
                <td>{index + 1}</td>
                <td>{eventNotice.message}</td>
                <td style={{ align: "center" }}>{format(new Date(eventNotice.startDate), 'dd-MM-yyyy')}</td>
                <td style={{ align: "center" }}>{format(new Date(eventNotice.endDate), 'dd-MM-yyyy')}</td>
                <td>
                  {eventNotice.attachment && (
                    <span
                      onClick={() => handleFilePreview(eventNotice.attachment, eventNotice.eventNoticeId)}
                      style={{
                        backgroundColor: 'rgb(177 237 255)',
                        color: 'rgb(21 14 188)',
                        padding: '4px 10px',
                        borderRadius: '12px',
                        cursor: 'pointer',
                      }}
                    >
                      {eventNotice.attachment || '-'}
                    </span>
                  )}
                </td>
                <td>
                  <button
                    className="btn btn-warning me-2"
                    onClick={() => handleEditClick(eventNotice.eventNoticeId)}
                  >
                    <FaEdit />
                  </button>
                  <button
                    className="btn btn-danger"
                    onClick={() => handleDeleteClick(eventNotice.eventNoticeId)}
                  >
                    <FaTrash />
                  </button>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="6" className="text-center">No event notices available</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );

  const renderMobileCards = () => (
    <div className="mobile-cards">
      {filteredEventNoticeList?.map((eventNotice, index) => (
        <div className="user-card" key={index}>
          <div className="card-row">
            <div className="card-label">Message</div>
            <div className="card-value">{eventNotice.message}</div>
          </div>
          <div className="card-row">
            <div className="card-label">Start Date</div>
            <div className="card-value">{format(new Date(eventNotice.startDate), 'dd-MM-yyyy')}</div>
          </div>
          <div className="card-row">
            <div className="card-label">End Date</div>
            <div className="card-value">{format(new Date(eventNotice.endDate), 'dd-MM-yyyy')}</div>
          </div>
          <div className="card-row">
            <div className="card-label">Attachment</div>
            <div className="card-value">
              {eventNotice.attachment && (
                <span
                  onClick={() => handleFilePreview(eventNotice.attachment, eventNotice.eventNoticeId)}
                  style={{
                    backgroundColor: 'rgb(177 237 255)',
                    color: 'rgb(21 14 188)',
                    padding: '4px 10px',
                    borderRadius: '12px',
                    cursor: 'pointer',
                  }}
                >
                  {eventNotice.attachment || '-'}
                </span>
              )}
            </div>
          </div>
          <div className="card-row">
            <div className="card-label">Action</div>
            <div className="card-value">
              <button
                className="btn btn-warning me-2"
                onClick={() => handleEditClick(eventNotice.eventNoticeId)}
              >
                <FaEdit/>
              </button>
              <button
                className="btn btn-danger"
                onClick={() => handleDeleteClick(eventNotice.eventNoticeId)}
              >
                <FaTrash/>
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );


  return (
    <div className="table-wrap container">
      <main>
        {currentAction === 'list' && (
          <>
            <div className="subscription-title mb-4">
              <h1>Event Notice List</h1>
            </div>
            {snackbar.open && <SnackbarUI message={snackbar.message} type={snackbar.type} />}

            <div id="card-body customized-card">

              <div className="row justify-content-end" style={{ display: 'flex', marginBottom: '20px' }}>
                <div className="col-md-3 mb-3" style={{ paddingRight: '10px' }}>
                  <div className="form-floating">
                    <div className="datepicker-container" style={{ position: 'relative' }}>
                      <label htmlFor="startDatePicker" style={{ marginLeft: '10px', width: '70px' }}>Start Date</label>
                      <DatePicker
                        id="startDatePicker"
                        selected={startDateFilter}
                        onChange={handleStartDateChange}
                        dateFormat="dd/MM/yyyy"
                        placeholderText="Start Date"
                        showYearDropdown
                        showMonthDropdown
                        scrollableYearDropdown
                        yearDropdownItemNumber={100}
                        className="form-control w-100"
                      />

                    </div>
                  </div>
                </div>

                <div className="col-md-3  mb-3" style={{ paddingLeft: '10px' }}>
                  <div className="form-floating">
                    <div className="datepicker-container" style={{ position: 'relative' }}>
                      <label htmlFor="endDatePicker" style={{ marginLeft: '10px', width: '90px' }}>End Date</label>
                      <DatePicker
                        id="endDatePicker"
                        selected={endDateFilter}
                        onChange={handleEndDateChange}
                        dateFormat="dd/MM/yyyy"
                        placeholderText="End Date"
                        showYearDropdown
                        showMonthDropdown
                        scrollableYearDropdown
                        yearDropdownItemNumber={100}
                        className="form-control w-100"
                        minDate={startDateFilter}
                      />

                    </div>
                  </div>
                </div>
              </div>


              <div className="table-container">
                {filteredEventNoticeList === null ? (
                  <div className="no-data">Loading data...</div>
                ) : filteredEventNoticeList?.length > 0 ? (
                  <>
                    {renderDesktopTable()}
                    {renderMobileCards()}
                  </>
                ) : (
                  <div className="no-data">
                    No events found within the selected date range
                  </div>
                )}



              </div>
            </div>
            <br />
            <button className="btn add btn-name" onClick={handleAddClick}>
              Add Event Notice
            </button>
          </>
        )}

        {(currentAction === "add" || currentAction === "edit") && (
          <div className="event-form-container">
            <div className="subscription-title mb-4">
              <h1>
                {currentAction === "add" ? "Add New Event Notice" : "Edit Event Notice"}
              </h1>
            </div>

            {snackbar.open && <SnackbarUI message={snackbar.message} type={snackbar.type} />}

            <Formik
              initialValues={selectedEvent || initialValuesOfEventNotice}
              validationSchema={validationSchemaEventNotice}
              onSubmit={handleEventNoticeSubmit}
              enableReinitialize
            >
              {({ values, errors, touched, setFieldValue }) => (
                <Form autoComplete="off">
                  <div className="row">
                    <div className="col-md-12 mb-5">
                      <div className="form-floating">
                        <div className="datepicker-container" style={{ display: 'flex', flexDirection: 'column' }}>
                          <label htmlFor="eventMessage" className="form-label label-wrap" style={{ marginBottom: '4px', color: '#6c757d' }}>
                            Event Message <span className="required-input">*</span>
                          </label>
                          <Field
                            as="textarea"
                            className={`form-control ${touched.message && errors.message ? "is-invalid" : ""
                              }`}
                            id="message"
                            name="message"
                            style={{ height: "130px" }}
                          />

                          <ErrorMessage
                            name="message"
                            component="div"
                            className="invalid-feedback"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-md-4 mb-3">
                      <div className="form-floating">
                        <div className="datepicker-container" style={{ display: 'flex', flexDirection: 'column' }}>
                          <label htmlFor="startDate" className="form-label label-wrap" style={{ marginBottom: '4px' }}>
                            Start Date <span className="required-input">*</span>
                          </label>
                          <DatePicker
                            id="startDate"
                            selected={values.startDate ? new Date(values.startDate) : null}
                            onChange={(date) => {
                              setFieldValue("startDate", date ? format(date, "yyyy-MM-dd") : "");
                            }}
                            dateFormat="dd/MM/yyyy"
                            placeholderText="Start Date"
                            showYearDropdown
                            showMonthDropdown
                            scrollableYearDropdown
                            yearDropdownItemNumber={100}
                            error={touched.startDate && errors.startDate ? errors.startDate : ""}
                            touched={touched.startDate}
                            className={`form-control w-100 ${touched.startDate && errors.startDate ? "is-invalid" : ""}`}
                          />
                          <ErrorMessage
                            name="startDate"
                            component="div"
                            className="invalid-feedback"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="col-md-4 mb-3">
                      <div className="form-floating">
                        <div className="datepicker-container" style={{ display: 'flex', flexDirection: 'column' }}>
                          <label htmlFor="endDate" className="form-label label-wrap" style={{ marginBottom: '4px' }}>
                            End Date <span className="required-input">*</span>
                          </label>
                          <DatePicker
                            id="endDate"
                            selected={values.endDate ? new Date(values.endDate) : null}
                            onChange={(date) => {
                              setFieldValue("endDate", date ? format(date, "yyyy-MM-dd") : "");
                            }}
                            dateFormat="dd/MM/yyyy"
                            placeholderText="End Date"
                            showYearDropdown
                            showMonthDropdown
                            scrollableYearDropdown
                            yearDropdownItemNumber={100}
                            minDate={values.startDate ? new Date(values.startDate) : null}
                            error={touched.endDate && errors.endDate ? errors.endDate : ""}
                            touched={touched.endDate}
                            className={`form-control w-100 ${touched.endDate && errors.endDate ? "is-invalid" : ""}`}
                          />
                          <ErrorMessage
                            name="endDate"
                            component="div"
                            className="invalid-feedback"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="col-md-4 mb-4">
                      <div className="datepicker-container" style={{ display: 'flex', flexDirection: 'column' }}>
                        <label htmlFor="attachmentFile" className="form-label label-wrap" style={{ marginBottom: '4px', color: '#6c757d' }}>
                          Attachment
                        </label>
                        <input
                          id="attachmentFile"
                          name="attachmentFile"
                          type="file"
                          onChange={(event) => {
                            setFieldValue("attachmentFile", event.currentTarget.files[0]);
                          }}
                          className="form-control"
                        />
                        {selectedEvent?.attachment && (
                          <div className="mt-2 me-0">
                            <span>Current file : </span>
                            <span
                              onClick={() => handleFilePreview(values.attachment)}
                              style={{
                                backgroundColor: 'rgb(177 237 255)',
                                color: 'rgb(21 14 188)',
                                padding: '4px 10px',
                                borderRadius: '12px',
                                cursor: 'pointer',
                              }}
                            >   {values.attachment || '-'}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="d-flex justify-content-center mt-4">
                    <button
                      type="button"
                      className="btn btn-secondary me-3"
                      onClick={handleCancel}
                      disabled={isSubmitting}
                    >
                      Cancel
                    </button>
                    {currentAction === "add" ?
                      <button type="submit" className="btn btn-success">
                        Submit
                      </button> :
                      <button type="submit" className="btn edit">
                        Update
                      </button>}

                  </div>
                </Form>
              )}
            </Formik>

            <ConfirmationDialog
              open={openEventNoticeConfirmationDialog}
              onClose={handleEventNoticeDialogClose}
              onConfirm={handlEventNoticeDialogConfirm}
              title="Confirm Submit"
              message="Are you sure you want to submit?"
            />
          </div>
        )}
        <ConfirmationDialog
          open={deleteEventNoticeConfirmationDialog}
          onClose={handleDeleteEventNoticeDialogClose}
          onConfirm={handleDeleteEventNoticeDialogConfirm}
          title="Confirm Delete"
          message="Are you sure you want to delete this event notice?"
        />
      </main>
    </div>
  );
};

export default EventNoticeComponent;